import React from 'react'
import { createPortal } from 'react-dom'
import { Icon } from './icons'

// ─── Popover ─────────────────────────────────────────────────────────
export interface PopoverProps {
  trigger: React.ReactElement | ((state: { open: boolean; setOpen: (v: boolean) => void }) => React.ReactNode)
  children?: React.ReactNode
  side?: 'top' | 'right' | 'bottom' | 'left'
  align?: 'start' | 'center' | 'end'
  offset?: number
  open?: boolean
  onOpenChange?: (open: boolean) => void
  width?: number | null
  panelStyle?: React.CSSProperties
  closeOnSelect?: boolean
}

export function Popover({
  trigger, children,
  side = 'bottom', align = 'start', offset = 6,
  open: controlledOpen, onOpenChange,
  width = null, panelStyle = {}, closeOnSelect = false,
}: PopoverProps) {
  const [uncontrolled, setUncontrolled] = React.useState(false)
  const open = controlledOpen ?? uncontrolled
  const setOpen = (v: boolean) => {
    if (controlledOpen == null) setUncontrolled(v)
    onOpenChange?.(v)
  }

  const triggerRef = React.useRef<HTMLSpanElement>(null)
  const panelRef = React.useRef<HTMLDivElement>(null)
  const [pos, setPos] = React.useState({ top: 0, left: 0 })

  React.useLayoutEffect(() => {
    if (!open) return
    const place = () => {
      const t = triggerRef.current?.getBoundingClientRect()
      const p = panelRef.current?.getBoundingClientRect()
      if (!t || !p) return
      const margin = 8
      const vw = window.innerWidth, vh = window.innerHeight
      // Auto-flip: if the preferred side doesn't have room but the opposite does, flip.
      let effectiveSide = side
      if (side === 'bottom' && (vh - t.bottom - margin) < (p.height + offset) && (t.top - margin) >= (p.height + offset)) {
        effectiveSide = 'top'
      } else if (side === 'top' && (t.top - margin) < (p.height + offset) && (vh - t.bottom - margin) >= (p.height + offset)) {
        effectiveSide = 'bottom'
      } else if (side === 'right' && (vw - t.right - margin) < (p.width + offset) && (t.left - margin) >= (p.width + offset)) {
        effectiveSide = 'left'
      } else if (side === 'left' && (t.left - margin) < (p.width + offset) && (vw - t.right - margin) >= (p.width + offset)) {
        effectiveSide = 'right'
      }
      let top = 0, left = 0
      if (effectiveSide === 'bottom') {
        top = t.bottom + offset
        left = align === 'start' ? t.left : align === 'end' ? t.right - p.width : t.left + (t.width - p.width) / 2
      } else if (effectiveSide === 'top') {
        top = t.top - p.height - offset
        left = align === 'start' ? t.left : align === 'end' ? t.right - p.width : t.left + (t.width - p.width) / 2
      } else if (effectiveSide === 'right') {
        left = t.right + offset
        top = align === 'start' ? t.top : align === 'end' ? t.bottom - p.height : t.top + (t.height - p.height) / 2
      } else if (effectiveSide === 'left') {
        left = t.left - p.width - offset
        top = align === 'start' ? t.top : align === 'end' ? t.bottom - p.height : t.top + (t.height - p.height) / 2
      }
      left = Math.max(margin, Math.min(left, vw - p.width - margin))
      top  = Math.max(margin, Math.min(top,  vh - p.height - margin))
      setPos({ top, left })
    }
    place()
    window.addEventListener('resize', place)
    window.addEventListener('scroll', place, true)
    return () => {
      window.removeEventListener('resize', place)
      window.removeEventListener('scroll', place, true)
    }
  }, [open, side, align, offset])

  React.useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      if (panelRef.current?.contains(e.target as Node) || triggerRef.current?.contains(e.target as Node)) return
      setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey) }
  }, [open])

  const triggerNode = typeof trigger === 'function'
    ? trigger({ open, setOpen })
    : React.cloneElement(trigger, {
        onClick: (e: React.MouseEvent) => {
          trigger.props.onClick?.(e)
          if (!e.defaultPrevented) setOpen(!open)
        },
        'aria-expanded': open,
        'aria-haspopup': true,
      })

  // Render the panel via a portal anchored to document.body. This is required
  // because an ancestor with `transform` (e.g. SidePeek's translateX animation
  // wrapping TaskDetailPeek's pickers) creates a containing block for
  // position:fixed descendants — without the portal, the panel positions
  // relative to the transformed ancestor instead of the viewport, which makes
  // dropdowns appear in the wrong place (looking like a second side peek over
  // the EDIT peek). Portaling to body sidesteps the containing-block issue
  // because the panel no longer has the transformed ancestor in its CSS
  // containing-block chain. (CSS spec: transform: none containing block.)
  const panel = open ? (
    <div ref={panelRef} role="dialog"
      onClick={(e) => { if (closeOnSelect) { e.stopPropagation(); setOpen(false) } }}
      style={{
        position: 'fixed', zIndex: 1000,
        top: pos.top, left: pos.left,
        background: 'var(--bg-surface)',
        border: '1px solid var(--border-strong)',
        borderRadius: 'var(--r-md)',
        boxShadow: '0 12px 32px rgba(0,0,0,0.18), 0 2px 8px rgba(0,0,0,0.08)',
        minWidth: width ?? 'auto',
        fontFamily: 'var(--font-sans)', color: 'var(--text-primary)',
        ...panelStyle,
      }}>
      {children}
    </div>
  ) : null

  return (
    <>
      <span ref={triggerRef} style={{ display: 'inline-flex' }}>{triggerNode}</span>
      {panel && typeof document !== 'undefined' ? createPortal(panel, document.body) : panel}
    </>
  )
}

// ─── DropdownMenu ────────────────────────────────────────────────────
export interface DropdownMenuItem {
  label?: string
  icon?: string
  kbd?: string
  onSelect?: () => void
  separator?: boolean
  header?: boolean
  tone?: 'default' | 'danger'
  disabled?: boolean
  checked?: boolean
}

export interface DropdownMenuProps {
  trigger: PopoverProps['trigger']
  items?: DropdownMenuItem[]
  side?: PopoverProps['side']
  align?: PopoverProps['align']
  width?: number
  /** Controlled open state — for menus with no clickable trigger element of
   *  their own (e.g. a right-click menu anchored to SVG click coordinates). */
  open?: boolean
  onOpenChange?: (open: boolean) => void
}

export function DropdownMenu({ trigger, items = [], side = 'bottom', align = 'start', width = 200, open, onOpenChange }: DropdownMenuProps) {
  return (
    <Popover trigger={trigger} side={side} align={align} width={width} closeOnSelect={true} open={open} onOpenChange={onOpenChange}>
      <div role="menu" style={{ padding: 4 }}>
        {items.map((it, i) => {
          if (it.separator) return <div key={i} role="separator" style={{ height: 1, background: 'var(--border)', margin: '4px 0' }}/>
          if (it.header) return (
            <div key={i} style={{
              padding: '6px 10px 4px',
              fontFamily: 'var(--font-sans)', fontSize: 10.5, fontWeight: 700,
              letterSpacing: 0.6, textTransform: 'uppercase', color: 'var(--text-muted)',
            }}>{it.label}</div>
          )
          const danger = it.tone === 'danger'
          return (
            <button key={i} role="menuitem"
              onMouseDown={() => it.onSelect?.()}
              disabled={it.disabled}
              style={{
                width: '100%', textAlign: 'left', cursor: 'pointer',
                background: 'transparent', border: 'none',
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '7px 10px', borderRadius: 'var(--r-sm)',
                color: danger ? 'var(--st-error)' : 'var(--text-primary)',
                fontFamily: 'var(--font-sans)', fontSize: 13, fontWeight: 500,
                opacity: it.disabled ? 0.4 : 1,
                transition: 'background var(--dur-fast) var(--ease-out)',
              }}
              onMouseEnter={(e) => { if (!it.disabled) e.currentTarget.style.background = 'var(--row-hover)' }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent' }}>
              {it.icon && <Icon name={it.icon} size={14} sw={1.7} style={{ color: danger ? 'var(--st-error)' : 'var(--text-muted)' }}/>}
              <span style={{ flex: 1 }}>{it.label}</span>
              {it.kbd && (
                <span style={{
                  fontFamily: 'var(--font-mono)', fontSize: 10.5, color: 'var(--text-dim)',
                  padding: '2px 5px', background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 4,
                }}>{it.kbd}</span>
              )}
              {it.checked && <Icon name="check" size={12} sw={2} style={{ color: 'var(--brand-gold)' }}/>}
            </button>
          )
        })}
      </div>
    </Popover>
  )
}
